import { useSyncExternalStore } from "react";
import { readPref, writePref } from "../data/persist";

// Per-screen preferences (kept in this browser only), e.g. a Pi kiosk that
// wants lighter effects while a laptop keeps them.

export interface DevicePrefs {
  reduceEffects: boolean;
  /** Wall-display mode: follow the morning/day/night schedule and return there when idle. */
  autoScreens: boolean;
  /** How much fits in each widget: "compact" trims padding and text a little (styles/tokens.css). */
  density: "comfortable" | "compact";
  /** Kinmantle's own keyboard for text boxes: "auto" when a field is tapped on a touchscreen without a system keyboard. */
  touchKeyboard: "auto" | "on" | "off";
  /** This screen is the wall computer, which keeps its own copy of the family photos (kiosk/files/kinmantle-photos). */
  wallComputer: boolean;
  /** Photo screensaver after this screen has been left alone a while. */
  screensaver: ScreensaverPrefs;
  /** What a wall display does at bedtime (src/app/Sleep.tsx). */
  sleep: SleepPrefs;
  /** Wall displays nudge everything a pixel or two now and then so nothing sits still for hours. */
  pixelShift: boolean;
}

export interface SleepPrefs {
  /**
   * "clock": everything black but a dim clock that wanders; "dim": that, with the
   * monitor's backlight turned down; "off": the monitor goes to standby until a touch;
   * "none": stay on the night screen. Dim and off need the wall computer's helper.
   */
  mode: "clock" | "dim" | "off" | "none";
  idleMinutes: number;
  /** Backlight for "dim", in percent of the monitor's range. */
  dimLevel: number;
}

export const SLEEP_DEFAULTS: SleepPrefs = { mode: "clock", idleMinutes: 5, dimLevel: 10 };

export interface ScreensaverPrefs {
  enabled: boolean;
  idleMinutes: number;
  /** "" = every wall photo; else a folder (album) or tag; "memories" = taken on this day in earlier years. */
  photos: string;
  transition: "fade" | "zoom" | "slide" | "none";
  secondsPerPhoto: number;
  clock: "off" | "corner" | "large";
  /** Stay off during the night screen hours (only screens that follow the schedule). */
  skipNight: boolean;
}

export const SCREENSAVER_DEFAULTS: ScreensaverPrefs = { enabled: false, idleMinutes: 10, photos: "", transition: "zoom", secondsPerPhoto: 20, clock: "corner", skipNight: true };

// Large screens are probably the wall; phones and small laptops shouldn't jump around.
const DEFAULTS: DevicePrefs = {
  reduceEffects: false,
  autoScreens: typeof window !== "undefined" && window.innerWidth >= 1100,
  density: "comfortable",
  touchKeyboard: "auto",
  wallComputer: false,
  screensaver: SCREENSAVER_DEFAULTS,
  sleep: SLEEP_DEFAULTS,
  pixelShift: true,
};
const saved = readPref<Partial<DevicePrefs>>("device", {});
let current: DevicePrefs = { ...DEFAULTS, ...saved, screensaver: { ...SCREENSAVER_DEFAULTS, ...saved.screensaver }, sleep: { ...SLEEP_DEFAULTS, ...saved.sleep } };

// The wall's kiosk launcher opens the app with ?wall=1 (kiosk/files/kinmantle-kiosk).
if (typeof window !== "undefined") {
  const url = new URL(window.location.href);
  if (url.searchParams.get("wall") === "1") {
    current = { ...current, wallComputer: true };
    writePref("device", current);
    url.searchParams.delete("wall");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  }
}
const listeners = new Set<() => void>();

export function setDevicePref<K extends keyof DevicePrefs>(key: K, value: DevicePrefs[K]) {
  current = { ...current, [key]: value };
  writePref("device", current);
  listeners.forEach((l) => l());
}

export const getDevicePrefs = () => current;

export function onDevicePrefsChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useDevicePrefs(): DevicePrefs {
  return useSyncExternalStore(onDevicePrefsChange, () => current);
}
